import React, { useEffect, useState } from "react";
import { addCareersData } from "../../services/data.service";
import { content as contentDefault, teaching as teachingDefault, geo } from "../../data/careers";
import { TextField, SelectField, TextareaField, FileField, ComboField } from "./fields";
import Stepper from "./Stepper";
import SuccessScreen from "./SuccessScreen";
import ReviewModal from "./ReviewModal";
import ConfirmDialog from "./ConfirmDialog";
import { IconBriefcase, IconUser, IconGraduation, IconChart, IconBook, IconEdit } from "./icons";
import {
  isEmpty, isValidDotsHyphens, isValidNumber, emailRe, mobileRe,
  makeRefId, dateBounds, validateResume,
} from "./helpers";

const stepIcons = [IconBriefcase, IconUser, IconGraduation, IconChart, IconBook];
const cgpaFilter = (v) => v === "" || /^\d{0,3}(\.\d{0,2})?$/.test(v);

const TeachingForm = ({ onExit, content = contentDefault, teaching = teachingDefault }) => {
  const { stepTitles, schools, specializationDepartments, phdStatuses, pgDegrees, pgBranches, ugDegrees } = teaching;
  const ALL_STEPS = stepTitles.map((_, i) => i + 1);
  const { max: maxDate, min: minDate, currentYear } = dateBounds();
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [errors, setErrors] = useState({});
  const [showReview, setShowReview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reviewErr, setReviewErr] = useState("");
  const [form, setForm] = useState({
    Applicationid: makeRefId("TE"), rectype: "Teaching",
    selcollege: "", selDep: "", selDepSpc: "", selectpost: "",
    fullname: "", email: "", phone: "", altphone: "", dob: "", address: "", city: "", dist: "", state: "", pincode: "",
    phdstatus: "", phdpassyear: "", phdDept: "", phdThesis: "", phdUniversity: "",
    selectpg: "", pg_branch: "", pg_other: "", specialization: "", CGPA: "", pgpass: "", institution_pg: "", university_pg: "", gaterank: "",
    selectug: "", ug_other: "", ugspecialization: "", ugCGPA: "", ugpass: "", institution_ug: "", university_ug: "", eamcet_rank: "",
    industrial_exp: "", academic_exp: "", total_exp: "", current_sal: "", expected_sal: "",
    njournals_count: "", nconference_count: "", injournals_count: "", inconference_count: "",
    scopus_id: "", orcid_id: "", books_published: "", patents: "", scopus_journals: "", sci_journals: "",
    attachment: "",
  });

  const school = schools.find((s) => s.name === form.selcollege);
  const departments = school?.departments || [];
  const postOptions = school?.posts || [];
  const specOptions = specializationDepartments[form.selDep] || [];
  const isTnP = form.selcollege === "Training & Placement";

  // total experience = industrial + academic
  useEffect(() => {
    const t = (parseInt(form.industrial_exp, 10) || 0) + (parseInt(form.academic_exp, 10) || 0);
    setForm((f) => (f.total_exp === String(t) ? f : { ...f, total_exp: String(t) }));
  }, [form.industrial_exp, form.academic_exp]);

  const set = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };
  const bind = (name, filter) => ({
    value: form[name], error: errors[name],
    onChange: (e) => { const v = e.target.value; if (!filter || filter(v)) set(name, v); },
  });

  const onSchoolChange = (e) => {
    setForm((f) => ({ ...f, selcollege: e.target.value, selDep: "", selDepSpc: "", selectpost: "" }));
    setErrors((er) => ({ ...er, selcollege: undefined }));
  };
  const onDepChange = (e) => {
    setForm((f) => ({ ...f, selDep: e.target.value, selDepSpc: "" }));
    setErrors((er) => ({ ...er, selDep: undefined }));
  };

  // Cascading location: State -> District; City suggestions filtered by State.
  const stateDistricts = geo.districts[form.state] || [];
  const stateCities = geo.cities[form.state] || [];
  const onStateChange = (e) => {
    setForm((f) => ({ ...f, state: e.target.value, dist: "", city: "" }));
    setErrors((er) => ({ ...er, state: undefined }));
  };
  const onDistrictChange = (e) => {
    setForm((f) => ({ ...f, dist: e.target.value }));
    setErrors((er) => ({ ...er, dist: undefined }));
  };

  const yearChecks = (field, v, label) => {
    if (isEmpty(v)) return `${label} is required`;
    if (!/^\d{4}$/.test(v)) return "Year must be 4 digits";
    if (+v > currentYear) return "Future years not allowed";
    if (+v < content.yearMin) return `Enter a valid year after ${content.yearMin}`;
    return "";
  };

  // Validate the given step(s). Defaults to the current wizard step; the review
  // modal passes ALL_STEPS to validate the whole application before submit.
  const validate = (stepsToCheck = [step]) => {
    const e = {};
    if (stepsToCheck.includes(1)) {
      if (isEmpty(form.selcollege)) e.selcollege = "Select where you're applying";
      if (isEmpty(form.selDep)) e.selDep = "Select Department";
      if (specOptions.length > 0 && isEmpty(form.selDepSpc)) e.selDepSpc = "Select Specialization";
      if (isEmpty(form.selectpost)) e.selectpost = "Select Post Type";
    }
    if (stepsToCheck.includes(2)) {
      if (isEmpty(form.fullname)) e.fullname = "Full Name is required";
      else if (form.fullname.trim().length < 4) e.fullname = "At least 4 characters";
      if (!emailRe.test(form.email)) e.email = "Valid email is required";
      if (!mobileRe.test(form.phone)) e.phone = "Valid 10-digit mobile is required";
      if (form.altphone && !/^\d{10}$/.test(form.altphone)) e.altphone = "Alternate mobile must be 10 digits";
      if (isEmpty(form.dob)) e.dob = "Date of Birth is required";
      if (isEmpty(form.address)) e.address = "Address is required";
      else if (form.address.trim().length < 10) e.address = "At least 10 characters";
      if (isEmpty(form.state)) e.state = "State is required";
      if (isEmpty(form.dist)) e.dist = "District is required";
      if (isEmpty(form.city)) e.city = "City is required";
      if (!/^\d{6}$/.test(form.pincode)) e.pincode = "Pincode must be 6 digits";
    }
    if (stepsToCheck.includes(3)) {
      if (!isTnP) {
        if (isEmpty(form.phdstatus)) e.phdstatus = "Select Ph.D Status";
        const phdActive = ["Awarded", "Submitted", "Registered"].includes(form.phdstatus);
        if (form.phdstatus === "Awarded") {
          const ye = yearChecks("phdpassyear", form.phdpassyear, "Year of Award");
          if (ye) e.phdpassyear = ye;
        }
        if (phdActive) {
          if (isEmpty(form.phdDept)) e.phdDept = "Department is required";
          if (isEmpty(form.phdThesis)) e.phdThesis = "Thesis Title is required";
          if (isEmpty(form.phdUniversity)) e.phdUniversity = "University is required";
        }
        if (isEmpty(form.selectpg)) e.selectpg = "Select PG Degree";
        if (form.selectpg === "M.Sc" && isEmpty(form.pg_branch)) e.pg_branch = "Select Branch";
        if (form.selectpg === "MA" && isEmpty(form.pg_branch)) e.pg_branch = "Select Branch";
        if (form.selectpg === "Other" && isEmpty(form.pg_other)) e.pg_other = "Specify Other PG Degree";
        if (isEmpty(form.specialization)) e.specialization = "Specialization is required";
        if (isEmpty(form.CGPA)) e.CGPA = "CGPA / Percentage is required";
        else if (isNaN(form.CGPA) || form.CGPA < 0 || form.CGPA > 100) e.CGPA = "Enter a value between 0 and 100";
        const pe = yearChecks("pgpass", form.pgpass, "Year of Passing");
        if (pe) e.pgpass = pe;
        else if (form.phdpassyear && +form.pgpass > +form.phdpassyear) e.pgpass = "PG year must be before PhD year";
        if (isEmpty(form.institution_pg)) e.institution_pg = "Institution is required";
        if (isEmpty(form.university_pg)) e.university_pg = "University is required";
      }
      // UG block (always)
      if (isEmpty(form.selectug)) e.selectug = "Select UG Degree";
      if (form.selectug === "Other" && isEmpty(form.ug_other)) e.ug_other = "Specify Other UG Degree";
      if (isEmpty(form.ugspecialization)) e.ugspecialization = "Specialization is required";
      if (isEmpty(form.ugCGPA)) e.ugCGPA = "CGPA / Percentage is required";
      else if (isNaN(form.ugCGPA) || form.ugCGPA < 0 || form.ugCGPA > 100) e.ugCGPA = "Enter a value between 0 and 100";
      const ue = yearChecks("ugpass", form.ugpass, "Year of Passing");
      if (ue) e.ugpass = ue;
      else if (!isTnP && form.pgpass && +form.ugpass >= +form.pgpass) e.ugpass = "UG year must be before PG year";
      if (isEmpty(form.institution_ug)) e.institution_ug = "Institution is required";
      if (isEmpty(form.university_ug)) e.university_ug = "University is required";
    }
    if (stepsToCheck.includes(4)) {
      if (isEmpty(form.industrial_exp)) e.industrial_exp = "Industrial experience is required";
      else if (+form.industrial_exp < 0 || +form.industrial_exp >= 50) e.industrial_exp = "Enter 0–49 years";
      if (isEmpty(form.academic_exp)) e.academic_exp = "Academic experience is required";
      else if (+form.academic_exp < 0 || +form.academic_exp >= 50) e.academic_exp = "Enter 0–49 years";
      if (isEmpty(form.current_sal)) e.current_sal = "Current salary is required";
      if (isEmpty(form.expected_sal)) e.expected_sal = "Expected salary is required";
    }
    if (stepsToCheck.includes(5)) {
      ["njournals_count", "nconference_count", "injournals_count", "inconference_count", "books_published", "patents", "scopus_journals", "sci_journals"].forEach((k) => {
        if (isEmpty(form[k])) e[k] = "Required";
        else if (isNaN(form[k]) || +form[k] < 0) e[k] = "Enter a valid number";
      });
      if (form.scopus_id && !/^\d{11}$/.test(form.scopus_id)) e.scopus_id = "Scopus ID must be 11 digits";
      if (form.orcid_id && !/^\d{4}-\d{4}-\d{4}-\d{4}$/.test(form.orcid_id)) e.orcid_id = "Format: 0000-0000-0000-0000";
      const fileErr = validateResume(form.attachment, content.file.maxKB);
      if (fileErr) e.attachment = fileErr;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) setStep((s) => s + 1); };
  const back = () => (step === 1 ? onExit && onExit() : setStep((s) => s - 1));

  // Last-step button (and Enter): advance, or open the review modal on the last step.
  const handleWizardSubmit = (ev) => {
    ev.preventDefault();
    if (step === ALL_STEPS.length) { validate(ALL_STEPS); setReviewErr(""); setSubmitErr(""); setShowReview(true); }
    else next();
  };

  // Review modal "Submit" -> validate everything, then ask for final confirmation.
  const onReviewSubmit = () => {
    if (validate(ALL_STEPS)) { setReviewErr(""); setConfirmOpen(true); }
    else setReviewErr("Please correct the highlighted fields before submitting.");
  };

  // Final confirmation "Yes" -> actually POST to the backend.
  const doSubmit = async () => {
    const { attachment, ...rest } = form;
    const data = new FormData();
    data.append("formData", JSON.stringify(rest));
    if (attachment) data.append("attachment", attachment);
    try {
      setLoading(true);
      setSubmitErr("");
      const res = await addCareersData(data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res) {
        setConfirmOpen(false);
        setShowReview(false);
        setSubmitted(true);
      } else {
        setConfirmOpen(false);
        setSubmitErr("Something went wrong. Please try again.");
      }
    } catch (err) {
      setConfirmOpen(false);
      setSubmitErr(err.response?.data?.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const orcidChange = (e) => {
    let v = e.target.value.replace(/\D/g, "").slice(0, 16);
    if (v.length > 4) v = v.replace(/^(\d{4})(\d+)/, "$1-$2");
    if (v.length > 9) v = v.replace(/^(\d{4}-\d{4})(\d+)/, "$1-$2");
    if (v.length > 14) v = v.replace(/^(\d{4}-\d{4}-\d{4})(\d+)/, "$1-$2");
    set("orcid_id", v);
  };

  // Fields for one step — reused by the wizard (one step) and the review modal (all steps).
  const sectionFields = (n) => {
    if (n === 1) return (
      <div className="crs-grid">
        <SelectField full label="Applying For" name="selcollege" required placeholder="Select School / Area"
          options={schools.map((s) => s.name)} value={form.selcollege} error={errors.selcollege} onChange={onSchoolChange} />
        <SelectField label="Department" name="selDep" required placeholder={form.selcollege ? "Select Department" : "Select School first"}
          options={departments} disabled={!form.selcollege} value={form.selDep} error={errors.selDep} onChange={onDepChange} />
        {specOptions.length > 0 && (
          <SelectField label="Specialization" name="selDepSpc" required options={specOptions} {...bind("selDepSpc")} />
        )}
        <SelectField full label="Post Type" name="selectpost" required placeholder={form.selcollege ? "Select Post" : "Select School first"}
          options={postOptions} disabled={!form.selcollege} {...bind("selectpost")} />
      </div>
    );
    if (n === 2) return (
      <div className="crs-grid">
        <TextField full label="Full Name" name="fullname" required placeholder="Full name" {...bind("fullname", isValidDotsHyphens)} />
        <TextField label="Email" name="email" type="email" required placeholder="you@example.com" {...bind("email")} />
        <TextField label="Mobile Number" name="phone" type="tel" inputMode="numeric" required maxLength={10} placeholder="10-digit mobile" {...bind("phone", (v) => isValidNumber(v, 10))} />
        <TextField label="Alternate Mobile" name="altphone" type="tel" inputMode="numeric" maxLength={10} placeholder="Optional" {...bind("altphone", (v) => isValidNumber(v, 10))} />
        <TextField label="Date of Birth" name="dob" type="date" required min={minDate} max={maxDate} {...bind("dob")} />
        <TextField label="Pincode" name="pincode" type="tel" inputMode="numeric" required maxLength={6} placeholder="6-digit pincode" {...bind("pincode", (v) => isValidNumber(v, 6))} />
        <TextareaField label="Address for Correspondence" name="address" required placeholder="Full address…" {...bind("address")} />
        <SelectField label="State" name="state" required placeholder="Select State"
          options={geo.states} value={form.state} error={errors.state} onChange={onStateChange} />
        <SelectField label="District" name="dist" required
          placeholder={form.state ? "Select District" : "Select State first"}
          options={stateDistricts} disabled={!form.state} value={form.dist} error={errors.dist} onChange={onDistrictChange} />
        <ComboField label="City" name="city" required
          placeholder={form.state ? "Type or select city" : "Select State first"}
          options={stateCities} disabled={!form.state} {...bind("city")} />
      </div>
    );
    if (n === 3) return (
      <div className="crs-grid">
        {!isTnP && (
          <>
            <SelectField full label="Ph.D Status" name="phdstatus" required options={phdStatuses} {...bind("phdstatus")} />
            {form.phdstatus === "Awarded" && (
              <TextField label="Year of Award (Ph.D)" name="phdpassyear" type="tel" inputMode="numeric" required maxLength={4} placeholder="YYYY" {...bind("phdpassyear", (v) => isValidNumber(v, 4))} />
            )}
            {["Awarded", "Submitted", "Registered"].includes(form.phdstatus) && (
              <>
                <TextField label="Department" name="phdDept" required placeholder="Department of…" {...bind("phdDept")} />
                <TextField full label="Thesis Title" name="phdThesis" required placeholder="Thesis title" {...bind("phdThesis")} />
                <TextField full label="University" name="phdUniversity" required placeholder="University" {...bind("phdUniversity")} />
              </>
            )}
            <SelectField label="PG Degree" name="selectpg" required options={pgDegrees} {...bind("selectpg")} />
            {(form.selectpg === "M.Sc" || form.selectpg === "MA") && (
              <SelectField label="PG Branch" name="pg_branch" required options={pgBranches[form.selectpg] || []} {...bind("pg_branch")} />
            )}
            {form.selectpg === "Other" && (
              <TextField label="Other PG Degree" name="pg_other" required placeholder="Specify" {...bind("pg_other")} />
            )}
            <TextField label="PG Specialization" name="specialization" required placeholder="Specialization" {...bind("specialization")} />
            <TextField label="PG Percentage / CGPA" name="CGPA" required placeholder="e.g. 78 or 8.2" {...bind("CGPA", cgpaFilter)} />
            <TextField label="PG Year of Passing" name="pgpass" type="tel" inputMode="numeric" required maxLength={4} placeholder="YYYY" {...bind("pgpass", (v) => isValidNumber(v, 4))} />
            <TextField label="PG Institution" name="institution_pg" required placeholder="Institution" {...bind("institution_pg")} />
            <TextField label="PG University" name="university_pg" required placeholder="University" {...bind("university_pg")} />
            <TextField label="GATE / GPAT Rank" name="gaterank" type="tel" inputMode="numeric" maxLength={6} placeholder="Optional" {...bind("gaterank", (v) => isValidNumber(v, 6))} />
          </>
        )}
        <SelectField label="UG Degree" name="selectug" required options={ugDegrees} {...bind("selectug")} />
        {form.selectug === "Other" && (
          <TextField label="Other UG Degree" name="ug_other" required placeholder="Specify" {...bind("ug_other")} />
        )}
        <TextField label="UG Specialization" name="ugspecialization" required placeholder="Specialization" {...bind("ugspecialization")} />
        <TextField label="UG Percentage / CGPA" name="ugCGPA" required placeholder="e.g. 75 or 8.0" {...bind("ugCGPA", cgpaFilter)} />
        <TextField label="UG Year of Passing" name="ugpass" type="tel" inputMode="numeric" required maxLength={4} placeholder="YYYY" {...bind("ugpass", (v) => isValidNumber(v, 4))} />
        <TextField label="UG Institution" name="institution_ug" required placeholder="Institution" {...bind("institution_ug")} />
        <TextField label="UG University" name="university_ug" required placeholder="University" {...bind("university_ug")} />
        <TextField label="EAMCET / ECET Rank" name="eamcet_rank" type="tel" inputMode="numeric" maxLength={6} placeholder="Optional" {...bind("eamcet_rank", (v) => isValidNumber(v, 6))} />
      </div>
    );
    if (n === 4) return (
      <div className="crs-grid">
        <TextField label="Industrial Experience (years)" name="industrial_exp" type="tel" inputMode="numeric" required maxLength={2} placeholder="e.g. 2" {...bind("industrial_exp", (v) => isValidNumber(v, 2))} />
        <TextField label="Academic Experience (years)" name="academic_exp" type="tel" inputMode="numeric" required maxLength={2} placeholder="e.g. 5" {...bind("academic_exp", (v) => isValidNumber(v, 2))} />
        <TextField label="Total Experience (years)" name="total_exp" value={form.total_exp} readOnly onChange={() => {}} />
        <div className="crs-field" />
        <TextField label="Current Salary (₹ / annum)" name="current_sal" type="tel" inputMode="numeric" required maxLength={8} placeholder="e.g. 600000" {...bind("current_sal", (v) => isValidNumber(v, 8))} />
        <TextField label="Expected Salary (₹ / annum)" name="expected_sal" type="tel" inputMode="numeric" required maxLength={8} placeholder="e.g. 800000" {...bind("expected_sal", (v) => isValidNumber(v, 8))} />
      </div>
    );
    if (n === 5) return (
      <div className="crs-grid">
        <TextField label="National Journal Papers" name="njournals_count" type="tel" inputMode="numeric" required maxLength={3} placeholder="0" {...bind("njournals_count", (v) => isValidNumber(v, 3))} />
        <TextField label="National Conference Papers" name="nconference_count" type="tel" inputMode="numeric" required maxLength={3} placeholder="0" {...bind("nconference_count", (v) => isValidNumber(v, 3))} />
        <TextField label="International Journal Papers" name="injournals_count" type="tel" inputMode="numeric" required maxLength={3} placeholder="0" {...bind("injournals_count", (v) => isValidNumber(v, 3))} />
        <TextField label="International Conference Papers" name="inconference_count" type="tel" inputMode="numeric" required maxLength={3} placeholder="0" {...bind("inconference_count", (v) => isValidNumber(v, 3))} />
        <TextField label="Scopus ID" name="scopus_id" type="tel" inputMode="numeric" maxLength={11} placeholder="11 digits (optional)" {...bind("scopus_id", (v) => isValidNumber(v, 11))} />
        <TextField label="ORCID ID" name="orcid_id" placeholder="0000-0000-0000-0000 (optional)" value={form.orcid_id} error={errors.orcid_id} onChange={orcidChange} />
        <TextField label="Books & Book Chapters" name="books_published" type="tel" inputMode="numeric" required maxLength={3} placeholder="0" {...bind("books_published", (v) => isValidNumber(v, 3))} />
        <TextField label="Patents" name="patents" type="tel" inputMode="numeric" required maxLength={3} placeholder="0" {...bind("patents", (v) => isValidNumber(v, 3))} />
        <TextField label="Scopus Journals" name="scopus_journals" type="tel" inputMode="numeric" required maxLength={3} placeholder="0" {...bind("scopus_journals", (v) => isValidNumber(v, 3))} />
        <TextField label="SCI Journals" name="sci_journals" type="tel" inputMode="numeric" required maxLength={3} placeholder="0" {...bind("sci_journals", (v) => isValidNumber(v, 3))} />
        <FileField
          full label="Upload Resume" name="attachment" required
          file={form.attachment instanceof File ? form.attachment : null}
          accept={content.file.accept} note={content.file.note} error={errors.attachment}
          onChange={(e) => set("attachment", e.target.files[0])}
        />
      </div>
    );
    return null;
  };

  if (submitted) return <SuccessScreen refid={form.Applicationid} formData={form} rectype="Teaching" />;

  const StepIcon = stepIcons[step - 1];

  return (
    <>
      <div className="crs-card">
        <div className="crs-card__head">
          <div>
            <p className="crs-card__head-eyebrow">Teaching Recruitment · Step {step} / {stepTitles.length}</p>
            <h2>{stepTitles[step - 1]}</h2>
          </div>
          <span className="crs-card__head-icon"><StepIcon /></span>
        </div>
        <div className="crs-card__body">
          <Stepper titles={stepTitles} current={step} />
          <form onSubmit={handleWizardSubmit} noValidate>
            {!showReview && sectionFields(step)}

            <div className="crs-nav">
              <button type="button" className="btn-ed btn-ed--ghost" onClick={back}>
                {step === 1 ? "Cancel" : "Back"}
              </button>
              <div className="crs-nav__spacer" />
              {step < stepTitles.length ? (
                <button key="nav-next" type="button" className="btn-ed btn-ed--primary" onClick={next}>Next</button>
              ) : (
                <button key="nav-review" type="submit" className="btn-ed btn-ed--primary">Review &amp; Submit</button>
              )}
            </div>
          </form>
        </div>
      </div>

      <ReviewModal
        open={showReview}
        title="Review Your Application"
        subtitle={`Teaching · ${form.Applicationid}`}
        onClose={() => setShowReview(false)}
      >
        <p className="crs-review-hint">
          <IconEdit /> Review your details below — click any field to edit. Scroll down to submit.
        </p>
        {stepTitles.map((t, i) => (
          <section className="crs-review-section" key={i}>
            <h3 className="crs-review-section__title">
              <span className="crs-review-section__no">{i + 1}</span>{t}
            </h3>
            {sectionFields(i + 1)}
          </section>
        ))}
        {(reviewErr || submitErr) && <div className="crs-alert" role="alert">{reviewErr || submitErr}</div>}
        <div className="crs-review-foot">
          <button type="button" className="btn-ed btn-ed--ghost" onClick={() => setShowReview(false)}>Back to Form</button>
          <button type="button" className="btn-ed btn-ed--primary" onClick={onReviewSubmit}>Submit Application</button>
        </div>
      </ReviewModal>

      <ConfirmDialog
        open={confirmOpen}
        loading={loading}
        title="Submit your application?"
        message="Please confirm all your details are correct. You won't be able to edit this application after submitting."
        confirmLabel="Yes, Submit"
        cancelLabel="Review Again"
        onConfirm={doSubmit}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default TeachingForm;
